<template>
  <div>
    <el-dialog
      v-bind="{
        closeOnPressEscape: false,
        closeOnClickModal: false,
        showClose: false,
      }"
      visible
      width="484px"
      custom-class="dialog-form"
    >
      <template #title>
        <h2>Edit client details</h2>
      </template>
      <tf-button
        id="btn-close"
        v-test:button="'edit done'"
        type="primary"
        icon="times"
        size="icon-no-fill"
        no-border
        no-branding
        @click="$emit('close')"
      />
      <form-employer
        v-if="isLoaded"
        v-model="model"
        v-bind="{
          disableFields,
          isSaving,
          showTitle: true,
        }"
        :is-valid.sync="isValid"
        @input="onInput"
        @submit="patchEmployerInfo"
      />
      <div
        slot="footer"
        class="btn-group"
      >
        <tf-button
          v-test:btn="'cancel'"
          class="cancel-close"
          size="text"
          type="secondary"
          no-branding
          @click="$emit('close')"
        >
          Cancel
        </tf-button>
        <tf-button
          v-test:btn="'status'"
          :disabled="isDisabled"
          :loading="isSaving"
          icon="check"
          type="primary"
          size="large"
          @click="patchEmployerInfo"
        >
          {{ model.status === 'active' ? 'Done' : 'Remove' }}
        </tf-button>
      </div>
    </el-dialog>
    <modal-notify
      :append-to-body="true"
      :visible.sync="showConfirmationCloseModal"
      type="warn"
      class="modal-notify"
      @close="showConfirmationCloseModal = false"
    >
      <icon
        slot="reference"
        name="exclamation-triangle"
      />
      <template #title>
        Are you sure?
      </template>
      <p>When a client is removed all active and past events will be closed and permanently removed</p>
      <tf-button
        slot="cta"
        v-test:button="'cancel'"
        class="cancel-close"
        size="large"
        type="secondary"
        no-branding
        @click="closeDisableModal"
      >
        Cancel
      </tf-button>
      <tf-button
        slot="cta"
        v-test:button="'confirm'"
        :loading="isSaving"
        size="large"
        type="decline"
        no-branding
        @click="disableEmployer"
      >
        Confirm
      </tf-button>
    </modal-notify>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import EmployerService from '@/services/employer';
  import FormEmployer from '@/components/Employer/Forms/Employer';
  import ModalNotify from '@/components/Modals/Notify';

  /**
   * ModalClientRecordEdit is used as a way for the broker to edit a client of record that is passed from its parent. The component relies on the store module to already be set with the data.
   *
   * @vuedoc
   * @exports Components/Employer/Edit
   * @category Components
   */
  export default {
    name: 'ModalClientRecordEdit',
    components: {
      FormEmployer,
      ModalNotify,
    },
    props: {
      /**
       * The ID of the employer/client used to interact with the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
    },
    data: () => ({
      showConfirmationCloseModal: false,
      isValid: true,
      isLoaded: false,
      isDirty: false,
      isSaving: false,
      model: {},
    }),
    computed: {
      ...mapState({
        // activeEvents
        activeEvents: (state) => state.activeEvents.activeEvents,
      }),
      /**
       * Evaluate if fields should be disabled depending on client status
       *
       * @returns {boolean}
       */
      disableFields() {
        return this.model.status === 'inactive';
      },
      /**
       * Evaluate validation and...uuuuuuum...dirtiness....to disable the form submission and CTA.
       *
       * @returns {boolean}
       */
      isDisabled() {
        return (!this.isValid || !this.isDirty);
      },
    },
    /**
     * Clone the employer info from the store. The BE returns 'Not provided' if nothing was entered previously. If that's the case, client_acquisition_year is set to '' first to display the
     */
    created() {
      const model = { ...this.$store.state.employer.employerInfo };

      if (model.client_acquisition_year.toLowerCase() === 'not provided') {
        model.client_acquisition_year = '';
      }

      if (model.benefit_administrator.toLowerCase() === 'not provided') {
        model.benefit_administrator = '';
      }

      if (model.tax_id.toLowerCase() === 'not provided') {
        model.tax_id = '';
      }

      if (model.sic_code.toLowerCase() === 'not provided') {
        model.sic_code = '';
      }

      this.$set(this, 'model', model);

      this.isLoaded = true;
    },
    methods: {
      ...mapActions(['updateEmployerInfo']),
      /**
       * Set is dirty appropriately to be used to disable the CTA.
       */
      onInput() {
        if (!this.isDirty) {
          this.isDirty = true;
        }
      },
      /**
       * Close the modal and set isSaving to false
       */
      closeDisableModal() {
        this.isSaving = false;
        this.showConfirmationCloseModal = false;
      },
      /**
       * Patch the appropriate employer with the model and update the store if successful, otherwise show an error message.
       */
      patchEmployerInfo() {
        if (this.isDisabled) {
          return;
        }

        if (this.model.status === 'inactive') {
          this.showConfirmationCloseModal = true;
        } else {
          this.updateEmployer();
        }
      },
      /**
       * Update the employer model
       */
      updateEmployer() {
        this.isSaving = true;

        EmployerService
          .patchEmployerInfo(this.clientId, this.model)
          .then(() => {
            this.showConfirmationCloseModal = false;
            // updateEmployerInfo with the model data
            this.updateEmployerInfo(this.model);

            this.$emit('close');
          })
          .catch(() => {
            this.showToast('There was a problem updating the client’s details.', 'error');
          })
          .finally(() => {
            this.isSaving = false;
          });
      },
      /**
       * Confirm the disabling of client and redirect user to 'MyClients' page
       */
      disableEmployer() {
        this.isSaving = true;

        EmployerService
          .patchClientDisable(this.clientId, this.model)
          .then(() => {
            this.showConfirmationCloseModal = false;
            // update the employer info status with model data after closing the confirm close modal
            // then call updateEmployerInfo with the model data
            this.updateEmployerInfo(this.model);

            // the model status 'active/inactive' will determine if the event will be closed
            if (this.model.status === 'inactive') {
              if (this.activeEvents.length) {
                this.showToast(
                  `${this.model.name} had active events that are closed. ThreeFlow has notified the invited carriers.`,
                );
              } else {
                this.showToast(
                  `${this.model.name} has been deactivated.`,
                );
              }

              this.$router.push({ name: 'MyClients' });
            }

            this.$emit('close');
          })
          .catch(() => {
            this.showToast('There was a problem updating the client’s details.', 'error');
          })
          .finally(() => {
            this.isSaving = false;
          });
      },
      /**
       * Render an error toast with the appropriate message.
       *
       * @param {string} message
       * @param {string} type
       */
      showToast(message, type = 'warning') {
        this.$message({
          duration: 3000,
          message,
          showClose: true,
          type,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  #btn-close {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  section {
    max-height: 443px;
    padding: 30px 40px 30px 0;
    overflow-y: scroll;
    box-sizing: border-box;
  }

  ::v-deep .cancel-close {
    /* stylelint-disable declaration-no-important */
    border-color: $tf-dark-gray !important;
    color: $tf-dark-gray !important;
    /* stylelint-enable declaration-no-important */
  }

  ::v-deep .dialog-form {
    max-height: calc(100vh - 100px);
    background: $tf-extra-light-gray-2;

    .el-dialog {
      &__body {
        padding: 0 0 0 40px;
      }

      &__footer {
        padding: 15px 55px 15px 40px;
        text-align: right;
        box-shadow: 0 1px 6px 0 $tf-light-gray;
      }

      &__header {
        padding: 40px 40px 20px;
        box-shadow: 0 -1px 6px 0 $tf-light-gray;
      }

      &__headerbtn {
        margin: 0;
      }
    }
  }
</style>
